<template>
    <v-dialog
        v-model="dialogModel"
        width="900"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        Emergency contact
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="emergencyContactForm" lazy-validation>
                    <h3>Health Screening Questionnaire and Confidential Details</h3>
                    <p class="mb-6 body-2">
                        Please complete your details and next of kin information. All fields marked with
                        <span class="red--text">*</span> are required.
                    </p>

                    <div class="mb-4" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">Your details</h2>
                    </div>

                    <v-row class="align-end mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">First name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.firstName"
                                placeholder="First name"
                                outlined
                                dense
                                :rules="firstNameRules"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Last name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.lastName"
                                placeholder="Last name"
                                outlined
                                dense
                                :rules="lastNameRules"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Address <span class="red--text">*</span></h3>
                            <v-textarea
                                v-model="form.address"
                                placeholder="Full address"
                                outlined
                                dense
                                rows="3"
                                auto-grow
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Tel. no. <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.telNo"
                                placeholder="Telephone"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Email <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.email"
                                placeholder="example@example.com"
                                type="email"
                                outlined
                                dense
                                :rules="emailRules"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Personal doctor — name and address</h3>
                            <v-textarea
                                v-model="form.personalDoctorNameAndAddress"
                                placeholder="GP name and surgery address"
                                outlined
                                dense
                                rows="3"
                                auto-grow
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Position <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.position"
                                placeholder="Job title / position"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Date of birth <span class="red--text">*</span></h3>
                            <v-menu
                                v-model="dobMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.dateOfBirthFormatted"
                                        placeholder="DD/MM/YYYY"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.dateOfBirth"
                                    :max="today"
                                    @input="dobMenu = false"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <h3 class="mb-2">Sex <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.sex" row :rules="[requiredRules]">
                                <v-radio label="Male" value="male" />
                                <v-radio label="Female" value="female" />
                            </v-radio-group>
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Nationality <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nationality"
                                placeholder="Nationality"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-2">Right to work in UK <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.rightToWorkInUk" row :rules="[requiredRules]">
                                <v-radio label="Yes" value="yes" />
                                <v-radio label="No" value="no" />
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <div class="mb-4 pt-4" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">SECTION B. EMERGENCY INFORMATION, Next of kin 1:</h2>
                    </div>

                    <v-row class="align-end mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">First name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin1FirstName"
                                placeholder="First name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Forename"
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Last name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin1LastName"
                                placeholder="Last name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Surname"
                                persistent-hint
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Relationship <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin1Relationship"
                                placeholder="Relationship"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="4" md="2">
                            <h3 class="mb-4">Mobile code</h3>
                            <v-text-field
                                v-model="form.nextOfKin1MobileCountryCode"
                                placeholder="+44"
                                outlined
                                dense
                                hint="Country code"
                                :persistent-hint="true"
                                type="number"
                            />
                        </v-col>
                        <v-col cols="8" md="4">
                            <h3 class="mb-4">Mobile number <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin1MobilePhoneNumber"
                                placeholder="Mobile"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <h3 class="mb-4">Home tel.</h3>
                            <v-text-field
                                v-model="form.nextOfKin1HomeTel"
                                placeholder="Home telephone"
                                outlined
                                dense
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <h3 class="mb-4">Work tel.</h3>
                            <v-text-field
                                v-model="form.nextOfKin1WorkTel"
                                placeholder="Work telephone"
                                outlined
                                dense
                                type="number"
                            />
                        </v-col>
                    </v-row>

                    <div class="mb-4 pt-4" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">Next of kin (2)</h2>
                    </div>

                    <v-row class="align-end mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">First name</h3>
                            <v-text-field
                                v-model="form.nextOfKin2FirstName"
                                placeholder="First name"
                                outlined
                                dense
                                hint="Forename"
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Last name</h3>
                            <v-text-field
                                v-model="form.nextOfKin2LastName"
                                placeholder="Last name"
                                outlined
                                dense
                                hint="Surname"
                                persistent-hint
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Relationship</h3>
                            <v-text-field
                                v-model="form.nextOfKin2Relationship"
                                placeholder="Relationship"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Mobile</h3>
                            <v-text-field
                                v-model="form.nextOfKin2Mobile"
                                placeholder="(000) 000-0000"
                                outlined
                                dense
                                type="number"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Home tel.</h3>
                            <v-text-field
                                v-model="form.nextOfKin2HomeTel"
                                placeholder="Home telephone"
                                outlined
                                dense
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Work tel.</h3>
                            <v-text-field
                                v-model="form.nextOfKin2WorkTel"
                                placeholder="Work telephone"
                                outlined
                                dense
                                type="number"
                            />
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import { mapActions } from 'vuex'
    import validationRules from '@/common/validationRules'

    const emptyForm = () => ({
        firstName: '',
        lastName: '',
        address: '',
        telNo: '',
        email: '',
        personalDoctorNameAndAddress: '',
        position: '',
        dateOfBirth: null,
        dateOfBirthFormatted: '',
        sex: '',
        nationality: '',
        rightToWorkInUk: '',
        nextOfKin1FirstName: '',
        nextOfKin1LastName: '',
        nextOfKin1Relationship: '',
        nextOfKin1MobileCountryCode: '',
        nextOfKin1MobilePhoneNumber: '',
        nextOfKin1HomeTel: '',
        nextOfKin1WorkTel: '',
        nextOfKin2FirstName: '',
        nextOfKin2LastName: '',
        nextOfKin2Relationship: '',
        nextOfKin2Mobile: '',
        nextOfKin2HomeTel: '',
        nextOfKin2WorkTel: ''
    })

    export default {
        name: 'EmergencyContactFormDialog',
        props: ['isOpen', 'programme'],

        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            dobMenu: false,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName,
            requiredRules: validationRules.required,
            emailRules: validationRules.email
        }),

        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            },
            today () {
                return new Date().toISOString().slice(0, 10)
            }
        },

        watch: {
            'form.dateOfBirth' (val) {
                this.form.dateOfBirthFormatted = val ? Date.formatDate([val]).trim() : ''
            }
        },

        methods: {
            ...mapActions([
                'uploadEmergencyContactForm',
                'showAlert'
            ]),
            resetForm () {
                this.form = emptyForm()
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.emergencyContactForm
                if (!formRef.validate()) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }
                try {
                    this.submitLoading = true
                    const payload = {
                        transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                        ...this.form,
                        formType: 'EmergencyContact'
                    }
                    await this.uploadEmergencyContactForm(payload)
                    this.$emit('saved')
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
